import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { useClustersRepository } from "../../../api/repositories/ClustersRepository";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import IconButton from "@mui/material/IconButton";
import {Button, Checkbox, Stack, Tooltip, useTheme} from "@mui/material";
import { AddRounded, ContentCopyRounded, DescriptionRounded, ModeRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { STATE_CLUSTERS_ACTIVE_FILTERS, STATE_CLUSTERS_TABLE_UPDATING } from "../constants/ClustersRecoil";
import FinanceDialog from "../../../component/FinanceDialog";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import ColouredChip from "../../../component/ColouredChip";
import { ID_ACTIVE, ID_INACTIVE } from "../constants/ClusterStatuses";
import { COLOR_ACTIVE, COLOR_INACTIVE } from "../constants/ClustersColors";
import ClustersModal from '../modals/ClustersModal';
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import { NumericSummary } from "../../../component/NumericSummary";
import dayjs from "dayjs";
import { useAgGridLocale, useHasScope } from "../../../../../config/globalConfig";
import { CREATE_MS_CLUSTER, DUPLICATE_MS_CLUSTER, EDIT_MS_CLUSTER, VIEW_CLUSTER_DETAIL } from "../../../utils/FinanceScope";
import {useClusterVoicesRepository} from "../../../api/repositories/ClusterVoicesRepository";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const ClustersTable = (props: any) => {

    const UNIQUE_ID = "fnn-clusters";

    const {t} = useTranslation("finance", {keyPrefix: "clusters"});
    const {getClusters, duplicateCluster, getSummary} = useClustersRepository();
    const {hasSegments} = useClusterVoicesRepository();
    const hasScope = useHasScope;
    const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);
    const filterDefs = useRecoilValue(STATE_CLUSTERS_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_CLUSTERS_TABLE_UPDATING);

    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const [openDialog, setOpenDialog] = useState(false);
    const [clusterHasSegments, setClusterHasSegments] = useState(false);
    const [clusterId, setClusterId] = useState();
    const [clusterDealer, setClusterDealer] = useState();
    const [clusterLegalEntity, setClusterLegalEntity] = useState();
    const [clusterName, setClusterName] = useState();
    const [openClusterModal, setOpenClusterModal] = useState(false);
    const [cluster, setCluster] = useState();

    const [checked, setChecked] = React.useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };


    const gridRef = useRef<AgGridReact>(null);
    const theme = useTheme();
    const navigate = useNavigate();
    const agGridLocale = useAgGridLocale();
    const notViewAction = !hasScope(VIEW_CLUSTER_DETAIL) && !hasScope(EDIT_MS_CLUSTER) && !hasScope(DUPLICATE_MS_CLUSTER);

    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");

    const columnDefs : ColDef[] = [
        {
            field: "id", headerName: t("tables.cluster.id"),
            minWidth: 100, maxWidth: 100,
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true,
        },
        {
            field: "msTypology", headerName: t("tables.cluster.ms-typology"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => t(`ms-types.${params.value}`)
        },
        {
            field: "name", headerName: t("tables.cluster.name"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "validFrom", headerName: t("tables.cluster.valid-from"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("ll") : '---',
        },
        {
            field: "validTo", headerName: t("tables.cluster.valid-to"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) =>  params.value ? dayjs(params.value).format("ll") : '---',
        },
        {
            field: "validStatus", headerName: t("tables.cluster.status"),
            minWidth: 150, maxWidth: 150,
            unSortIcon: true, sortable: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.validStatus;
                switch (status) {
                    case ID_ACTIVE:
                        return {
                            component: () => <ColouredChip name={ID_ACTIVE} color={COLOR_ACTIVE}/>
                        };
                    case ID_INACTIVE:
                        return {
                            component: () => <ColouredChip name={ID_INACTIVE} color={COLOR_INACTIVE}/>
                        };
                    default:
                        break;
                }
            },
        },
        {
            field: "legalEntity", headerName: t("tables.cluster.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.cluster.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {

                if (notViewAction) return {};

                return {
                    component: () => (
                        <FinanceDynamicActions>
                            {hasScope(VIEW_CLUSTER_DETAIL) &&
                            <Tooltip arrow title={t('tables.cluster.actions.tooltips.show-detail')}>
                                <IconButton
                                    sx={{color: theme.palette.accent.main}}
                                    onClick={() => {
                                        window.open(`/finance/clusters/${params.data.id}`, "_blank");
                                    }}
                                >
                                    <DescriptionRounded/>
                                </IconButton>
                            </Tooltip>
                            }
                            {hasScope(EDIT_MS_CLUSTER) &&
                            <Tooltip arrow title={t('tables.cluster.actions.tooltips.edit')}>
                                <IconButton
                                    sx={{color: theme.palette.accent.main}}
                                    onClick={() => {
                                        setOpenClusterModal(true);
                                        setCluster(params.data);
                                    }}
                                >
                                    <ModeRounded/>
                                </IconButton>
                            </Tooltip>
                            }
                            {hasScope(DUPLICATE_MS_CLUSTER) &&
                            <Tooltip arrow title={t('tables.cluster.actions.tooltips.duplicate')}>
                                <IconButton
                                    sx={{color: theme.palette.accent.main}}
                                    onClick={() => {
                                        hasSegments(params.data.id).then((res) => setClusterHasSegments(res.result));
                                        setOpenDialog(true);
                                        setClusterId(params.data.id)
                                        setClusterDealer(params.data.dealer)
                                        setClusterLegalEntity(params.data.legalEntity)
                                        setClusterName(params.data.name)
                                    }}
                                >
                                    <ContentCopyRounded/>
                                </IconButton>
                            </Tooltip>
                            }
                        </FinanceDynamicActions>
                    ),
                };
            },
            //cellClass: "grid-cell-actions",
            //headerClass: "grid-header-actions",
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {title: t('tables.cluster.no-rows-overlay.title')}
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort;

                getClusters(startRow, endRow - startRow, sort, order, filterDefs)
                    .then((response) => {
                        let result = response.result;

                        if (result.page.totalElements === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.page.totalElements,
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const toolbar = {
        renderInfo: () => (
            <NumericSummary query={() => getSummary(filterDefs)} fields={[ID_ACTIVE, ID_INACTIVE]} i18nPath={"clusters.counters"}/>
        ),
        renderActions: () => (
            <Stack>
                {hasScope(CREATE_MS_CLUSTER) &&
                <Button fullWidth color={"accent"} variant="contained" component="label"
                        startIcon={<AddRounded/>}
                        onClick={() => {
                            navigate("/finance/clusters/wizard")
                        }}>
                    {t('buttons.add-cluster')}
                </Button>
                }
            </Stack>
        )
    }
    return (
        <>
            <AgGridCustomTable
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowSelection={"multiple"}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                toolbar={toolbar}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    setLocalGridState(UNIQUE_ID, {
                        rows: gridRef.current!.api?.paginationGetPageSize(),
                        cols: getLocalGridState(UNIQUE_ID).cols
                    });
                }}
            ></AgGridCustomTable>
            <FinanceDialog title={t('tables.cluster.dialog.title')}
                           content={
                                <>
                                    {!clusterHasSegments  &&
                                        <>
                                            { t('tables.cluster.dialog.contentSegments')  }
                                            <Checkbox
                                                checked={checked}
                                                onChange={handleChange}
                                                inputProps={{'aria-label': 'controlled'}}
                                            />
                                        </>
                                   }
                                    <p>{t('tables.cluster.dialog.content', {clusterName: clusterName})}</p>
                                    <p><i>{t('tables.cluster.dialog.contentNote')}</i></p>
                                </>
                            }
                           cancelButton={t('tables.cluster.dialog.cancel')}
                           open={openDialog}
                           confirmButton={t('tables.cluster.dialog.confirm')}
                           onClose={() => {
                               setOpenDialog(false);
                               setChecked(false);
                           }}
                           callback={() => {
                               if (clusterId !== undefined)
                                   duplicateCluster(clusterId, {
                                       enabledSegment: checked,
                                       legalEntity: clusterLegalEntity,
                                       dealer: clusterDealer
                                   }).then((res) => {
                                       if(res.status === 'KO'){
                                           dispatchGlobalData({
                                               type: setSnackbar,
                                               payload: {
                                                   snackbar: {
                                                       message: t('error-dialog.errors.'+res.message),
                                                       type: snackbarTypes.error,
                                                       show: true,
                                                       autoClose: true,
                                                   },
                                               },
                                           });
                                       } else {
                                           setUpdating(true)
                                       }
                                   });
                           }}
                           titleIcon={<InfoRoundedIcon sx={{color: 'accent.main'}}/>}/>
            <ClustersModal open={openClusterModal} onClose={() => {
                setOpenClusterModal(false)
            }} cluster={cluster}/>
        </>
    );
};

export default ClustersTable;